import { forwardRef, useImperativeHandle, useState, useRef, useEffect, useCallback } from "react";
import { Modal } from "antd";
import { useSelector } from "react-redux";
import { usePDFViewer } from "../../../hooks/UserHooks/MyCoursesHook/usePDFViewer";
import PDFControlsHeader from "./component/PDFControlsHeader";
import PDFViewerArea from "./component/PDFViewArea";
import PDFFooter from "./component/PDFFooter";
import "../../../components/user/myCourses/UserPDFReaderModal.css";

const ADMIN_ROLES = ["ADMIN", "ADMIN_SUPPORT"];
const TRANSITION_SECONDS = 5;

const UserPDFReaderModal = forwardRef(({
  isOpen, fileName, onClose, contentId, courseId,
  blockTime, handleClose, isAcknowledged, onSuccess, isContentCompleted, markSidebarItemAcknowledged,
}, ref) => {
  const roles = useSelector((state) => state.auth?.roles ?? []);
  const currentPortal = useSelector((state) => state.auth?.currentPortal);
  const isAdminRole = currentPortal === "admin" && roles.some((r) => ADMIN_ROLES.includes(r));

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [countdown, setCountdown] = useState(TRANSITION_SECONDS);
  const timerRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const proceedToNext = useCallback(() => {
    clearTimer();
    setIsTransitioning(false);
    if (typeof onSuccess === "function") onSuccess();
  }, [onSuccess]);

  const delayedOnSuccess = useCallback(() => {
    setCountdown(TRANSITION_SECONDS);
    setIsTransitioning(true);

    clearTimer();
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          if (typeof onSuccess === "function") onSuccess();
          setIsTransitioning(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [onSuccess]);

  useEffect(() => clearTimer, []); 

  const pdf = usePDFViewer({
    isOpen, fileName, blockTime, isAcknowledged,
    contentId, courseId, handleClose, onClose,
    onSuccess: delayedOnSuccess,
    markSidebarItemAcknowledged,
  });

  useImperativeHandle(ref, () => ({ saveProgress: pdf.saveProgress }));

  const handleFinalSubmitWithWarning = (page) => {
    if (!pdf.hasAgreed) {
      Modal.warning({
        title: "Terms and Conditions Required",
        content: "You must acknowledge the content before submitting. Please check the agreement box to proceed.",
        okText: "OK",
        zIndex: 1000,
      });
      return;
    }
    pdf.handleFinalSubmit(page);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={pdf.containerRef}
      className="show-content-container"
      style={{ pointerEvents: pdf.isExitingFullscreen ? 'none' : 'auto', position: 'relative' }}
    >
      <PDFControlsHeader
        displayName={pdf.displayName}
        remainingTime={pdf.remainingTime}
        loading={pdf.loading}
        error={pdf.error}
        pdfFullyLoaded={pdf.pdfFullyLoaded}
        isFullScreen={pdf.isFullScreen}
        isExitingFullscreen={pdf.isExitingFullscreen}
        isCloseDisabled={isAdminRole ? false : pdf.isCloseDisabled}
        onToggleFullscreen={pdf.toggleFullScreen}
        onClose={pdf.handleModalClose}
      />

      <PDFViewerArea
        loading={pdf.loading}
        loadingProgress={pdf.loadingProgress}
        error={pdf.error}
        pdfBlobUrl={pdf.pdfBlobUrl}
        displayName={pdf.displayName}
        canvasRef={pdf.canvasRef}
        onRetry={pdf.retryLoading}
      />

      <PDFFooter
        currentPage={pdf.currentPage}
        numPages={pdf.numPages}
        isExitingFullscreen={pdf.isExitingFullscreen}
        nextTimerActive={isAdminRole ? false : pdf.nextTimerActive}
        nextTimer={isAdminRole ? 0 : pdf.nextTimer}
        onPrev={pdf.prevPage}
        onNext={pdf.nextPage}
        onSubmit={() => handleFinalSubmitWithWarning(pdf.currentPage)}
        showNavigation={!pdf.loading && !pdf.error && !!pdf.numPages}
        isAcknowledged={isAcknowledged}
        hasAgreed={isAdminRole ? true : pdf.hasAgreed}
        onAgree={() => pdf.setHasAgreed(true)}
        showSubmitButton={!isAdminRole}
        showCloseConfirm={isAdminRole ? false : pdf.showCloseConfirm}
        closeConfirmType={pdf.closeConfirmType}
        onConfirmClose={pdf.handleConfirmClose}
        onCancelClose={pdf.handleCancelClose}
        isSubmitting={pdf.isSubmitting}
      />

      {isTransitioning && (
        <div
          className="absolute inset-0 z-[1100] flex flex-col items-center justify-center bg-black/80 text-white p-6 text-center animate-fadeIn pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-16 h-16 bg-blue-500/20 text-blue-400 rounded-full flex items-center justify-center mb-4 border border-blue-500/40 shadow-lg font-bold text-2xl">
            {countdown}
          </div>

          <h3 className="text-xl font-semibold mb-2 text-white">Next content in {countdown}s</h3>
          <p className="text-gray-300 text-sm max-w-sm mb-6 leading-relaxed">
            You've submitted this document. Transitioning to the next module automatically.
          </p>

          <button
            onClick={proceedToNext}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#910E11] hover:bg-[#700b0d] text-white rounded-lg text-sm font-medium transition-colors shadow-md"
          >
            Next Content
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
});

export default UserPDFReaderModal;